import { useEffect, useRef } from 'react';

import * as Sentry from '@sentry/react';

import { vnextApiClient } from '@/services/vnext-api';
import type { RegistryOrderDetail } from '@/types/registry-documents';
import { toError } from '@/utils/errors';
import { usePoll } from './usePoll';

/** A download link is presigned for an hour. The detail is re-read well
 * inside that, so a list left open never offers a dead link. */
export const REGISTRY_ORDER_DETAIL_REFRESH_MS = 30 * 60_000;

/** After a failed read the next one comes soon. The poll never retries
 * faster than its cadence, and half an hour would outlast the links. */
export const REGISTRY_ORDER_DETAIL_RETRY_MS = 30_000;

/**
 * One order with the documents delivered against it, each with a fresh
 * download link.
 *
 * Read while `orderId` is set (the row is expanded) and again whenever
 * `revision` changes: the caller passes what the list says about the
 * order (its received count, its last update), so a delivery that lands
 * while the row is open shows up without a reload.
 */
export function useRegistryOrderDetail(orderId: string | null, revision: string | number = 0) {
  // The order whose last read failed; null once a read succeeds. The poll
  // keeps a failure until its next success, across a change of order and
  // across being switched off, so the failure is kept here against the
  // order it was for: another order, or this one read afresh, starts
  // clean and reads as loading until its own read answers.
  const failedForRef = useRef<string | null>(null);
  const poll = usePoll<RegistryOrderDetail>({
    interval: () =>
      failedForRef.current !== null
        ? REGISTRY_ORDER_DETAIL_RETRY_MS
        : REGISTRY_ORDER_DETAIL_REFRESH_MS,
    enabled: orderId !== null,
    refetchDeps: [orderId, revision],
    queryFn: async () => {
      const detail = await vnextApiClient.getRegistryOrder(orderId as string);
      failedForRef.current = null;
      return detail;
    },
    // Only a read that was not superseded gets here, so the order it was
    // for is the one asked for now.
    onError: (err) => {
      failedForRef.current = orderId;
      Sentry.captureException(toError(err));
    },
  });

  // Switched off (the row collapsed, the dialog closed): the next read of
  // the same order is a fresh one, not a retry of this failure.
  useEffect(() => {
    if (orderId === null) {
      failedForRef.current = null;
    }
  }, [orderId]);

  // The poll keeps its last payload across a change of order.
  const detail = poll.data && poll.data.id === orderId ? poll.data : undefined;
  const failed = poll.error !== undefined && failedForRef.current === orderId;

  return {
    detail,
    isLoading: orderId !== null && detail === undefined && !failed,
    error: failed ? toError(poll.error) : null,
    refetch: poll.refetch,
  };
}
